import React, { useState, useEffect, useCallback } from 'react';
import {
  FiX, FiUser, FiHome, FiPhone, FiCheckSquare, FiCoffee, FiDollarSign, FiFileText, FiSliders, FiPlus, FiTrash2,
  FiEye, FiPrinter,
} from 'react-icons/fi';
import { viewIndividualInvoice, printIndividualInvoice } from '../utils/invoicePrint';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: '2-digit' }) : '—');
const money = (n) => `PKR ${Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const normalizeName = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.04em', opacity: 0.55, marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13.5px', fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div style={{ marginBottom: '18px' }}>
      <h4 style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', opacity: 0.75 }}>
        <Icon /> {title}
      </h4>
      {children}
    </div>
  );
}

// Read-only "complete view" of a hostel resident — used identically by the
// admin Hostel Admissions page and the hostel portal's own Residents page.
// Data-fetching functions are injected so this component stays agnostic to
// which API surface (admin vs hostel-scoped) the caller uses.
const emptyAdjForm = { adjustment_type: 'charge', amount: '', period_start: '', period_end: '', reason: '' };

export default function ResidentProfileModal({
  admission, campuses = [], courses = [], onClose, fetchAttendance, fetchMeals, fetchPayments, fetchInvoices,
  fetchInvoiceDetail, fetchAdjustments, canManageAdjustments = false, onAddAdjustment, onDeleteAdjustment,
  fetchResidentHistory,
}) {
  const [attendance, setAttendance] = useState(null);
  const [meals, setMeals] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [residentHistory, setResidentHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjForm, setAdjForm] = useState(emptyAdjForm);
  const [adjSaving, setAdjSaving] = useState(false);
  const [adjError, setAdjError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [attRes, mealRes, payRes, invRes, adjRes] = await Promise.all([
        fetchAttendance(admission.id).catch(() => ({ data: [] })),
        fetchMeals(admission.id).catch(() => ({ data: [] })),
        fetchPayments(admission.id).catch(() => ({ data: [] })),
        fetchInvoices(admission.id).catch(() => ({ data: [] })),
        fetchAdjustments ? fetchAdjustments(admission.id).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      const attRows = attRes.data || [];
      setAttendance({
        present: attRows.filter(r => r.status === 'present').length,
        absent: attRows.filter(r => r.status === 'absent').length,
        total: attRows.length,
      });
      const mealRows = mealRes.data || [];
      setMeals({
        breakfast: mealRows.filter(r => r.breakfast).length,
        lunch: mealRows.filter(r => r.lunch).length,
        dinner: mealRows.filter(r => r.dinner).length,
        daysLogged: mealRows.length,
      });
      setPayments(payRes.data || []);
      setInvoices(invRes.data || []);
      setAdjustments(adjRes.data || []);
      if (fetchResidentHistory && admission.cnic) {
        try {
          const histRes = await fetchResidentHistory(admission.cnic);
          setResidentHistory((histRes.data || []).filter(r => r.id !== admission.id));
        } catch (e) { setResidentHistory([]); }
      }
    } catch (e) { /* non-fatal — sections just show empty */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admission.id]);

  useEffect(() => { load(); }, [load]);

  const handleAddAdjustment = async () => {
    if (!adjForm.amount || !adjForm.period_start || !adjForm.period_end) {
      setAdjError('Amount, period start and period end are required'); return;
    }
    setAdjSaving(true); setAdjError('');
    try {
      await onAddAdjustment({ admission_id: admission.id, ...adjForm, amount: Number(adjForm.amount) });
      setAdjForm(emptyAdjForm);
      setShowAdjForm(false);
      load();
    } catch (e) {
      setAdjError(e.response?.data?.error || 'Failed to save adjustment');
    }
    setAdjSaving(false);
  };

  const handleDeleteAdjustment = async (id) => {
    if (!window.confirm('Remove this adjustment?')) return;
    try {
      await onDeleteAdjustment(id);
      load();
    } catch (e) { /* non-fatal */ }
  };

  const [invoiceActionError, setInvoiceActionError] = useState('');
  const handleViewInvoice = async (id) => {
    try { const res = await fetchInvoiceDetail(id); viewIndividualInvoice(res.data); }
    catch (e) { setInvoiceActionError('Failed to load invoice'); }
  };
  const handlePrintInvoice = async (id) => {
    try { const res = await fetchInvoiceDetail(id); printIndividualInvoice(res.data); }
    catch (e) { setInvoiceActionError('Failed to load invoice'); }
  };

  const campusName = campuses.find(c => c.id === admission.campus_id)?.name;
  const courseName = courses.find(c => c.id === admission.course_id)?.name;
  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalInvoiced = invoices.reduce((s, i) => s + Number(i.total_amount || 0), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '640px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title"><FiUser /> {admission.full_name}</h3>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
          <span className={`badge ${admission.status === 'active' ? 'badge-success' : 'badge-muted'}`}>{admission.status}</span>
          {admission.cnic && <span style={{ fontSize: '12.5px', opacity: 0.7 }}>{admission.cnic}</span>}
        </div>

        <Section icon={FiUser} title="Personal Details">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Father's Name" value={admission.father_name} />
            <Field label="Gender" value={admission.gender} />
            <Field label="Date of Birth" value={fmtDate(admission.dob)} />
            <Field label="Dept / Batch / Program" value={admission.dept_batch_program} />
            <Field label="Campus" value={campusName} />
            <Field label="Course" value={courseName} />
            <Field label="Guardian" value={admission.guardian_name ? `${admission.guardian_name} (${admission.guardian_relationship || '—'})` : null} />
            <Field label="Guardian Contact" value={admission.guardian_contact} />
          </div>
        </Section>

        <Section icon={FiPhone} title="Contact">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Phone" value={admission.contact_no} />
            <Field label="Email" value={admission.email} />
            <Field label="Address" value={admission.permanent_address} />
          </div>
        </Section>

        <Section icon={FiHome} title="Admission">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Date of Admission" value={fmtDate(admission.date_of_admission)} />
            <Field label="Date of Exit" value={admission.date_of_exit ? fmtDate(admission.date_of_exit) : '—'} />
            <Field label="Monthly Rent" value={money(admission.monthly_rent)} />
            <Field label="Notes" value={admission.notes} />
          </div>
        </Section>

        {loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : (
          <>
            {residentHistory && residentHistory.length > 0 && (
              <Section icon={FiHome} title="Other Stays (same CNIC, across all hostels)">
                <p style={{ fontSize: '11.5px', opacity: 0.6, marginTop: '-4px', marginBottom: '8px' }}>
                  Each stay is its own independent record — editing one never changes another. Shown here just to see this person's full history in one place.
                </p>
                <div style={{ display: 'grid', gap: '4px' }}>
                  {residentHistory.map(r => {
                    const nameMismatch = normalizeName(r.full_name) !== normalizeName(admission.full_name);
                    return (
                      <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px', padding: '5px 0', borderBottom: '1px solid rgba(128,128,128,0.15)', gap: '8px' }}>
                        <span style={{ flex: 1 }}>
                          <strong>{r.hostel_name}</strong> — {fmtDate(r.date_of_admission)} to {r.date_of_exit ? fmtDate(r.date_of_exit) : 'present'}{' '}
                          <span className={`badge ${r.status === 'active' ? 'badge-success' : 'badge-muted'}`} style={{ marginLeft: '4px' }}>{r.status}</span>
                          <div style={{ opacity: 0.7, marginTop: '2px' }}>
                            Recorded as: {r.full_name}
                            {nameMismatch && (
                              <span className="badge badge-gold" style={{ marginLeft: '6px' }} title="This stay's name doesn't match the record you're currently viewing — worth checking which one is correct.">
                                ⚠ Name differs
                              </span>
                            )}
                          </div>
                        </span>
                        <span style={{ textAlign: 'right' }}>
                          Dues {money(r.total_dues)} · Paid {money(r.total_paid)} · <strong style={{ color: r.balance > 0 ? '#ef4444' : undefined }}>Bal {money(r.balance)}</strong>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            <Section icon={FiCheckSquare} title="Attendance Summary (all recorded days)">
              <div style={{ display: 'flex', gap: '18px', fontSize: '13px' }}>
                <span>Present: <strong>{attendance?.present ?? 0}</strong></span>
                <span>Absent: <strong>{attendance?.absent ?? 0}</strong></span>
                <span>Total Marked: <strong>{attendance?.total ?? 0}</strong></span>
              </div>
            </Section>

            <Section icon={FiCoffee} title="Meals Summary (all recorded days)">
              <div style={{ display: 'flex', gap: '18px', fontSize: '13px' }}>
                <span>Breakfast: <strong>{meals?.breakfast ?? 0}</strong></span>
                <span>Lunch: <strong>{meals?.lunch ?? 0}</strong></span>
                <span>Dinner: <strong>{meals?.dinner ?? 0}</strong></span>
              </div>
            </Section>

            <Section icon={FiDollarSign} title="Payment History">
              {payments.length === 0 ? (
                <p style={{ fontSize: '12.5px', opacity: 0.6 }}>No payments recorded.</p>
              ) : (
                <>
                  <div style={{ maxHeight: '120px', overflowY: 'auto', display: 'grid', gap: '4px' }}>
                    {payments.map(p => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', padding: '4px 0', borderBottom: '1px solid rgba(128,128,128,0.15)' }}>
                        <span>{fmtDate(p.payment_date)}{p.months_covered ? ` — ${p.months_covered}` : ''}</span>
                        <strong>{money(p.amount)}</strong>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '13px', marginTop: '6px' }}>Total Paid: <strong>{money(totalPaid)}</strong></div>
                </>
              )}
            </Section>

            <Section icon={FiFileText} title="Invoice History">
              {invoiceActionError && <div className="alert alert-error" style={{ fontSize: '12px', padding: '6px 10px', marginBottom: '8px' }}>{invoiceActionError}</div>}
              {invoices.length === 0 ? (
                <p style={{ fontSize: '12.5px', opacity: 0.6 }}>No invoices generated.</p>
              ) : (
                <>
                  <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'grid', gap: '4px' }}>
                    {invoices.map(i => (
                      <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px', padding: '4px 0', borderBottom: '1px solid rgba(128,128,128,0.15)', gap: '8px' }}>
                        <span style={{ flex: 1, minWidth: 0 }}>{i.invoice_number} ({fmtDate(i.period_start)} – {fmtDate(i.period_end)})</span>
                        <strong>{money(i.total_amount)}</strong>
                        {fetchInvoiceDetail && (
                          <span style={{ display: 'flex', gap: '4px' }}>
                            <button className="btn btn-small btn-secondary" title="View invoice" onClick={() => handleViewInvoice(i.id)}><FiEye size={12} /></button>
                            <button className="btn btn-small btn-secondary" title="Print invoice" onClick={() => handlePrintInvoice(i.id)}><FiPrinter size={12} /></button>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '13px', marginTop: '6px' }}>Total Invoiced: <strong>{money(totalInvoiced)}</strong></div>
                </>
              )}
            </Section>

            <Section icon={FiSliders} title="Billing Adjustments (manual overrides / waivers)">
              {adjustments.length === 0 ? (
                <p style={{ fontSize: '12.5px', opacity: 0.6 }}>No manual adjustments recorded.</p>
              ) : (
                <div style={{ display: 'grid', gap: '4px', marginBottom: '10px' }}>
                  {adjustments.map(a => (
                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px', padding: '4px 0', borderBottom: '1px solid rgba(128,128,128,0.15)' }}>
                      <span>
                        <span className={`badge ${a.adjustment_type === 'credit' ? 'badge-success' : 'badge-gold'}`} style={{ marginRight: '6px' }}>{a.adjustment_type}</span>
                        {fmtDate(a.period_start)} – {fmtDate(a.period_end)}{a.reason ? ` — ${a.reason}` : ''}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{money(a.amount)}</strong>
                        {canManageAdjustments && (
                          <button onClick={() => handleDeleteAdjustment(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }} title="Remove"><FiTrash2 size={13} /></button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {canManageAdjustments && (
                showAdjForm ? (
                  <div style={{ background: 'rgba(128,128,128,0.08)', borderRadius: '8px', padding: '10px', marginTop: '8px' }}>
                    {adjError && <div className="alert alert-error" style={{ fontSize: '12px', padding: '6px 10px' }}>{adjError}</div>}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                      <select className="form-control" value={adjForm.adjustment_type} onChange={e => setAdjForm(f => ({ ...f, adjustment_type: e.target.value }))}>
                        <option value="charge">Charge (add to bill)</option>
                        <option value="credit">Credit (waive / reduce bill)</option>
                      </select>
                      <input type="number" min="0" className="form-control" placeholder="Amount (PKR)" value={adjForm.amount} onChange={e => setAdjForm(f => ({ ...f, amount: e.target.value }))} />
                      <input type="date" className="form-control" value={adjForm.period_start} onChange={e => setAdjForm(f => ({ ...f, period_start: e.target.value }))} />
                      <input type="date" className="form-control" value={adjForm.period_end} onChange={e => setAdjForm(f => ({ ...f, period_end: e.target.value }))} />
                    </div>
                    <input type="text" className="form-control" placeholder="Reason (optional)" value={adjForm.reason} onChange={e => setAdjForm(f => ({ ...f, reason: e.target.value }))} style={{ marginBottom: '8px' }} />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-primary btn-small" onClick={handleAddAdjustment} disabled={adjSaving}>{adjSaving ? 'Saving...' : 'Save Adjustment'}</button>
                      <button className="btn btn-secondary btn-small" onClick={() => { setShowAdjForm(false); setAdjError(''); }}>Cancel</button>
                    </div>
                    <p style={{ fontSize: '11px', opacity: 0.6, marginTop: '6px' }}>Applies automatically to any invoice generated for a period overlapping these dates.</p>
                  </div>
                ) : (
                  <button className="btn btn-secondary btn-small" onClick={() => setShowAdjForm(true)}><FiPlus /> Add Adjustment</button>
                )
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
